import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import {
  PIXEL_PAST_RESCUE_LOGICAL_WIDTH,
  PIXEL_PAST_RESCUE_LOGICAL_HEIGHT,
  PIXEL_PAST_RESCUE_WORLD_HEIGHT,
  PIXEL_PAST_RESCUE_RISE_GRAVITY,
  PIXEL_PAST_RESCUE_FALL_GRAVITY,
  PIXEL_PAST_RESCUE_JUMP_VELOCITY,
  PIXEL_PAST_RESCUE_ICE_MAX_SPEED,
  PIXEL_PAST_RESCUE_ICE_ACCEL,
  PIXEL_PAST_RESCUE_ICE_GROUND_FRICTION,
  PIXEL_PAST_RESCUE_ICE_AIR_FRICTION,
  PIXEL_PAST_RESCUE_JUMP_FLIP_SPEED,
  PIXEL_PAST_RESCUE_JUMP_SOUND_SRC,
  PIXEL_PAST_RESCUE_DIE_SOUND_SRC,
  PIXEL_PAST_RESCUE_WIN_PICK_VALUE,
  PIXEL_PAST_RESCUE_LOSS_PICK_VALUE,
  PIXEL_PAST_RESCUE_MAX_LIVES,
  PIXEL_PAST_RESCUE_PLATFORMS,
  PIXEL_PAST_RESCUE_OBSTACLES,
  PIXEL_PAST_RESCUE_MONSTERS,
  PIXEL_PAST_RESCUE_GOAL,
  PIXEL_PAST_RESCUE_PLAYER_SPAWN,
  computePixelPastCameraY,
  pixelPastRectsOverlap,
} from './pixelPastRescueLayout';
import {
  moveBoxHorizontal,
  moveBoxVertical,
  stepPixelPastMonsters,
  applyPixelPastGravity,
  applyPixelPastIceHorizontalVelocity,
} from './pixelPastRescuePhysics';
import './PixelPastRescueMinigame.css';

const SOLIDS = [...PIXEL_PAST_RESCUE_PLATFORMS, ...PIXEL_PAST_RESCUE_OBSTACLES];

const MOVE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'a', 'A', 'd', 'D']);
const JUMP_KEYS = new Set(['ArrowUp', ' ', 'w', 'W']);

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} cameraY
 * @param {{ x: number, y: number, w: number, h: number }} player
 * @param {typeof PIXEL_PAST_RESCUE_MONSTERS} monsters
 * @param {number} jumpFlipAngle
 */
function drawPixelPastScene(ctx, cameraY, player, monsters, jumpFlipAngle) {
  ctx.save();
  ctx.translate(0, -cameraY);

  const skyGrad = ctx.createLinearGradient(0, 0, 0, PIXEL_PAST_RESCUE_WORLD_HEIGHT);
  skyGrad.addColorStop(0, '#7ec8e8');
  skyGrad.addColorStop(0.45, '#b8e8ff');
  skyGrad.addColorStop(1, '#dff6ff');
  ctx.fillStyle = skyGrad;
  ctx.fillRect(0, 0, PIXEL_PAST_RESCUE_LOGICAL_WIDTH, PIXEL_PAST_RESCUE_WORLD_HEIGHT);

  for (const cloudX of [40, 140, 240, 60, 200]) {
    const cloudY = 40 + (cloudX % 3) * 24;
    ctx.fillStyle = 'rgba(255,255,255,0.72)';
    ctx.fillRect(cloudX, cloudY, 28, 8);
    ctx.fillRect(cloudX + 8, cloudY - 6, 20, 8);
  }

  for (const surface of [...PIXEL_PAST_RESCUE_PLATFORMS, ...PIXEL_PAST_RESCUE_OBSTACLES]) {
    ctx.fillStyle = '#8ed4f8';
    ctx.fillRect(surface.x, surface.y, surface.w, surface.h);
    ctx.fillStyle = '#c8eeff';
    ctx.fillRect(surface.x, surface.y, surface.w, Math.min(5, surface.h));
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillRect(surface.x + 2, surface.y + 2, Math.max(2, surface.w - 4), 2);
    for (let sparkleX = surface.x + 6; sparkleX < surface.x + surface.w - 4; sparkleX += 14) {
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillRect(sparkleX, surface.y + Math.min(8, surface.h - 3), 2, 2);
    }
    ctx.fillStyle = '#6bbee0';
    ctx.fillRect(surface.x, surface.y + surface.h - 3, surface.w, 3);
  }

  const goal = PIXEL_PAST_RESCUE_GOAL;
  drawPixelPastPrincess(ctx, goal.x, goal.y, goal.w, goal.h);

  for (const monster of monsters) {
    ctx.fillStyle = '#3d5c1f';
    ctx.fillRect(monster.x, monster.y + 4, monster.w, monster.h - 4);
    ctx.fillStyle = '#6b8c42';
    ctx.fillRect(monster.x + 2, monster.y, monster.w - 4, monster.h - 6);
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(monster.x + 3, monster.y + 4, 3, 3);
    ctx.fillRect(monster.x + monster.w - 6, monster.y + 4, 3, 3);
  }

  drawPixelPastHero(ctx, player.x, player.y, player.w, player.h, jumpFlipAngle);

  ctx.restore();
}

/**
 * Princess sprite (14×16) — long hair, crown, pink dress; same proportions as the rescuer.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 */
function drawPixelPastPrincess(ctx, x, y, w, h) {
  ctx.fillStyle = '#fbbf24';
  ctx.fillRect(x + 4, y, 6, 2);
  ctx.fillRect(x + 5, y, 4, 1);

  ctx.fillStyle = '#fde68a';
  ctx.fillRect(x + 1, y + 1, 3, 6);
  ctx.fillRect(x + w - 4, y + 1, 3, 7);
  ctx.fillRect(x + 3, y + 1, w - 6, 2);

  ctx.fillStyle = '#f5d0c5';
  ctx.fillRect(x + 4, y + 4, 6, 3);
  ctx.fillStyle = '#1e1b4b';
  ctx.fillRect(x + 5, y + 5, 1, 1);
  ctx.fillRect(x + 8, y + 5, 1, 1);
  ctx.fillStyle = '#e879a9';
  ctx.fillRect(x + 6, y + 6, 2, 1);

  ctx.fillStyle = '#db2777';
  ctx.fillRect(x + 3, y + 8, w - 6, 4);
  ctx.fillStyle = '#f472b6';
  ctx.fillRect(x + 2, y + 12, w - 4, 3);
  ctx.fillStyle = '#fbcfe8';
  ctx.fillRect(x + 4, y + 9, 2, 2);
  ctx.fillRect(x + w - 6, y + 13, 2, 1);

  ctx.fillStyle = '#fce7f3';
  ctx.fillRect(x + 3, y + h - 1, 3, 1);
  ctx.fillRect(x + w - 6, y + h - 1, 3, 1);
}

/**
 * @param {object} game
 */
function respawnPixelPastPlayer(game) {
  Object.assign(game.player, PIXEL_PAST_RESCUE_PLAYER_SPAWN);
  game.vx = 0;
  game.vy = 0;
  game.onGround = true;
  game.airJumpUsed = false;
  game.jumpFlipAngle = 0;
  game.jumpFlipSpinning = false;
  game.cameraY = computePixelPastCameraY(PIXEL_PAST_RESCUE_PLAYER_SPAWN.y);
}

function startPixelPastJump(game) {
  game.vy = PIXEL_PAST_RESCUE_JUMP_VELOCITY;
  game.onGround = false;
  game.jumpFlipAngle = 0;
  game.jumpFlipSpinning = true;
  playSoundEffect(PIXEL_PAST_RESCUE_JUMP_SOUND_SRC);
}

/**
 * Hooded rescuer (purple cloak, cyan trim) — distinct from classic red/blue platformer heroes.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 * @param {number} flipRadians
 */
function drawPixelPastHero(ctx, x, y, w, h, flipRadians = 0) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  ctx.save();
  ctx.translate(cx, cy);
  if (flipRadians !== 0) {
    ctx.rotate(flipRadians);
  }
  ctx.translate(-w / 2, -h / 2);

  ctx.fillStyle = '#4c1d95';
  ctx.fillRect(2, 0, w - 4, 6);
  ctx.fillStyle = '#22d3ee';
  ctx.fillRect(5, 2, 4, 2);
  ctx.fillStyle = '#c4b5fd';
  ctx.fillRect(3, 1, 2, 2);
  ctx.fillRect(w - 5, 1, 2, 2);

  ctx.fillStyle = '#d4a574';
  ctx.fillRect(4, 5, 6, 3);
  ctx.fillStyle = '#1e1b4b';
  ctx.fillRect(5, 6, 1, 1);
  ctx.fillRect(8, 6, 1, 1);

  ctx.fillStyle = '#6d28d9';
  ctx.fillRect(1, 8, w - 2, 5);
  ctx.fillStyle = '#a78bfa';
  ctx.fillRect(2, 9, 3, 3);
  ctx.fillStyle = '#5b21b6';
  ctx.fillRect(w - 4, 9, 2, 4);

  ctx.fillStyle = '#334155';
  ctx.fillRect(2, 13, 4, 3);
  ctx.fillRect(8, 13, 4, 3);
  ctx.fillStyle = '#67e8f9';
  ctx.fillRect(2, 15, 4, 1);
  ctx.fillRect(8, 15, 4, 1);

  ctx.restore();
}

/**
 * @param {object} props
 * @param {{ winPickValue: number, lossPickValue: number }} props.challenge
 * @param {(pickedTreeIndex: number) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string } | null} props.winReward
 */
export default function PixelPastRescueMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const submittedRef = useRef(false);
  const keysRef = useRef(new Set());
  const gameRef = useRef(null);

  const challengeReady =
    challenge != null &&
    Number.isInteger(challenge.winPickValue) &&
    Number.isInteger(challenge.lossPickValue) &&
    Number.isInteger(challenge.maxLives);

  const winPickValue = challengeReady ? challenge.winPickValue : PIXEL_PAST_RESCUE_WIN_PICK_VALUE;
  const lossPickValue = challengeReady ? challenge.lossPickValue : PIXEL_PAST_RESCUE_LOSS_PICK_VALUE;
  const maxLives = challengeReady ? challenge.maxLives : PIXEL_PAST_RESCUE_MAX_LIVES;

  const [livesRemaining, setLivesRemaining] = useState(maxLives);
  const [lifeLostMessage, setLifeLostMessage] = useState(null);

  useEffect(() => {
    if (lifeLostMessage == null) {
      return undefined;
    }
    const id = window.setTimeout(() => setLifeLostMessage(null), 2200);
    return () => window.clearTimeout(id);
  }, [lifeLostMessage]);

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.pixelPastRescue.winReward.${winReward.id}`
      : null;

  const setPlayArenaRef = useCallback(
    (node) => {
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  const submitPick = useCallback(
    (pickValue) => {
      if (submittedRef.current || disabled || resultOutcome != null) {
        return;
      }
      submittedRef.current = true;
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      onPickTree(pickValue);
    },
    [disabled, onPickTree, resultOutcome]
  );

  useEffect(() => {
    if (!challengeReady || resultOutcome != null) {
      return undefined;
    }
    submittedRef.current = false;
    setLivesRemaining(maxLives);
    setLifeLostMessage(null);
    gameRef.current = {
      player: { ...PIXEL_PAST_RESCUE_PLAYER_SPAWN },
      vx: 0,
      vy: 0,
      onGround: true,
      monsters: PIXEL_PAST_RESCUE_MONSTERS.map((m) => ({ ...m })),
      cameraY: computePixelPastCameraY(PIXEL_PAST_RESCUE_PLAYER_SPAWN.y),
      airJumpUsed: false,
      jumpFlipAngle: 0,
      jumpFlipSpinning: false,
      livesRemaining: maxLives,
    };

    const onKeyDown = (e) => {
      if (MOVE_KEYS.has(e.key) || JUMP_KEYS.has(e.key)) {
        e.preventDefault();
      }
      keysRef.current.add(e.key);
      if (JUMP_KEYS.has(e.key)) {
        if (e.repeat) {
          return;
        }
        const game = gameRef.current;
        if (!game || submittedRef.current) {
          return;
        }
        if (game.onGround) {
          startPixelPastJump(game);
          game.airJumpUsed = false;
        } else if (!game.airJumpUsed) {
          startPixelPastJump(game);
          game.airJumpUsed = true;
        }
      }
    };
    const onKeyUp = (e) => {
      keysRef.current.delete(e.key);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    const tick = () => {
      const canvas = canvasRef.current;
      const game = gameRef.current;
      if (!canvas || !game || submittedRef.current) {
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('PixelPastRescueMinigame: canvas 2d context unavailable');
      }

      const keys = keysRef.current;
      let moveDir = 0;
      if (keys.has('ArrowLeft') || keys.has('a') || keys.has('A')) {
        moveDir -= 1;
      }
      if (keys.has('ArrowRight') || keys.has('d') || keys.has('D')) {
        moveDir += 1;
      }
      game.vx = applyPixelPastIceHorizontalVelocity(
        game.vx,
        moveDir,
        game.onGround,
        PIXEL_PAST_RESCUE_ICE_ACCEL,
        PIXEL_PAST_RESCUE_ICE_MAX_SPEED,
        PIXEL_PAST_RESCUE_ICE_GROUND_FRICTION,
        PIXEL_PAST_RESCUE_ICE_AIR_FRICTION
      );

      game.vy = applyPixelPastGravity(
        game.vy,
        PIXEL_PAST_RESCUE_RISE_GRAVITY,
        PIXEL_PAST_RESCUE_FALL_GRAVITY
      );

      const player = game.player;
      const dx = moveBoxHorizontal(player, SOLIDS, game.vx);
      player.x += dx;

      const vert = moveBoxVertical(player, SOLIDS, game.vy);
      player.y += vert.deltaY;
      if (vert.onGround) {
        game.vy = 0;
        game.onGround = true;
        game.airJumpUsed = false;
        game.jumpFlipSpinning = false;
        game.jumpFlipAngle = 0;
      } else if (vert.deltaY !== game.vy && game.vy > 0) {
        game.vy = 0;
        game.onGround = true;
        game.airJumpUsed = false;
        game.jumpFlipSpinning = false;
        game.jumpFlipAngle = 0;
      } else {
        game.onGround = false;
      }

      if (game.jumpFlipSpinning) {
        game.jumpFlipAngle += PIXEL_PAST_RESCUE_JUMP_FLIP_SPEED;
        if (game.jumpFlipAngle >= Math.PI * 2) {
          game.jumpFlipAngle = 0;
          game.jumpFlipSpinning = false;
        }
      }

      game.monsters = stepPixelPastMonsters(game.monsters);
      for (const monster of game.monsters) {
        if (pixelPastRectsOverlap(player, monster)) {
          playSoundEffect(PIXEL_PAST_RESCUE_DIE_SOUND_SRC);
          game.livesRemaining -= 1;
          const nextLives = game.livesRemaining;
          setLivesRemaining(nextLives);
          if (nextLives <= 0) {
            submitPick(lossPickValue);
            return;
          }
          setLifeLostMessage(nextLives);
          respawnPixelPastPlayer(game);
          break;
        }
      }

      if (pixelPastRectsOverlap(player, PIXEL_PAST_RESCUE_GOAL)) {
        submitPick(winPickValue);
        return;
      }

      game.cameraY = computePixelPastCameraY(player.y + player.h / 2);

      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, PIXEL_PAST_RESCUE_LOGICAL_WIDTH, PIXEL_PAST_RESCUE_LOGICAL_HEIGHT);
      drawPixelPastScene(ctx, game.cameraY, player, game.monsters, game.jumpFlipAngle);

      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    if (containerRef?.current && typeof containerRef.current.focus === 'function') {
      containerRef.current.focus();
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      keysRef.current.clear();
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [challengeReady, resultOutcome, submitPick, winPickValue, lossPickValue, maxLives]);

  const promptText =
    resultOutcome === 'won'
      ? winRewardBodyKey
        ? t(winRewardBodyKey, {
            percent: winReward?.darknessReductionApplied,
            xp: winReward?.xpGained,
            billyBucks: winReward?.billyBucksAmount,
            health: winReward?.startHealthBonus != null ? winReward.startHealthBonus : 3,
          })
        : t('exploreUnknownPage.minigame.pixelPastRescue.success')
      : resultOutcome === 'lost'
        ? t('exploreUnknownPage.minigame.pixelPastRescue.failure')
        : t('exploreUnknownPage.minigame.pixelPastRescue.playPrompt');

  if (!challengeReady) {
    return (
      <div
        ref={setPlayArenaRef}
        className="pixel-past-rescue-play-arena pixel-past-rescue-play-arena--challenge-error"
        role="alert"
      >
        <p>{t('exploreUnknownPage.minigame.pixelPastRescue.missingChallenge')}</p>
      </div>
    );
  }

  return (
    <div
      ref={setPlayArenaRef}
      className="pixel-past-rescue-play-arena"
      role="application"
      tabIndex={0}
      aria-label={t('exploreUnknownPage.minigame.pixelPastRescue.playArenaAria')}
      onMouseDown={(e) => {
        e.currentTarget.focus();
      }}
    >
      {resultOutcome == null ? (
        <div className="pixel-past-rescue-prompt-strip">
          <h2 className="pixel-past-rescue-title">{t('exploreUnknownPage.minigame.pixelPastRescue.title')}</h2>
          <p>{promptText}</p>
          <p className="pixel-past-rescue-lives">
            {t('exploreUnknownPage.minigame.pixelPastRescue.livesRemaining', { count: livesRemaining })}
          </p>
          <p className="pixel-past-rescue-controls-hint">
            {t('exploreUnknownPage.minigame.pixelPastRescue.controlsHint')}
          </p>
          {lifeLostMessage != null ? (
            <p className="pixel-past-rescue-life-lost-flash" role="status">
              {t('exploreUnknownPage.minigame.pixelPastRescue.lifeLost', { count: lifeLostMessage })}
            </p>
          ) : null}
        </div>
      ) : null}
      <canvas
        ref={canvasRef}
        className="pixel-past-rescue-canvas"
        width={PIXEL_PAST_RESCUE_LOGICAL_WIDTH}
        height={PIXEL_PAST_RESCUE_LOGICAL_HEIGHT}
        aria-hidden
      />
      {resultOutcome != null ? (
        <div className="pixel-past-rescue-result-overlay" role="presentation">
          <div
            className={`pixel-past-rescue-result-banner pixel-past-rescue-result-banner--${resultOutcome}`}
            role="status"
            aria-live="polite"
          >
            <p className="pixel-past-rescue-result-banner-title">
              {resultOutcome === 'won'
                ? t('exploreUnknownPage.minigame.pixelPastRescue.successTitle')
                : t('exploreUnknownPage.minigame.pixelPastRescue.failureTitle')}
            </p>
            <p className="pixel-past-rescue-result-banner-body">
              {resultOutcome === 'won'
                ? winRewardBodyKey
                  ? t(winRewardBodyKey, {
                      percent: winReward?.darknessReductionApplied,
                      xp: winReward?.xpGained,
                      billyBucks: winReward?.billyBucksAmount,
                      health: winReward?.startHealthBonus != null ? winReward.startHealthBonus : 3,
                    })
                  : t('exploreUnknownPage.minigame.pixelPastRescue.successBodyGeneric')
                : t('exploreUnknownPage.minigame.pixelPastRescue.failureBody')}
            </p>
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className="pixel-past-rescue-result-proceed"
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
